import { type ReactNode, useState } from 'react'
import { RJSFSchema } from '@rjsf/utils'
import {
  Mtds,
  ArrowDownIcon,
  ArrowUpIcon,
  LockIcon,
  PlusIcon,
  TrashIcon
} from 'prove-common'
import {
  buildSubSamplePropertyList,
  type SchemaProperty
} from '../../utils/requisitionSchemaUtils'
import {
  getPropertiesFromSchema,
  getSelectableWidgets,
  getWidgetDescription,
  getWidgetSubProperties,
  getWidgetTitle,
  getWidgetTypeIcon,
  groupWidgets,
  SchemaWidget,
  SchemaWidgetGroup,
  sortWidgets,
  turnPropertiesToWidgets,
  WidgetState
} from '../../schemas/schema-utils'
import { SchemaElementPickerModal } from './SchemaElementPickerModal'
import { payloadWidgets } from '../../schemas/payload-widgets'
import conditionals from '../../../../../../schemas/conditionals.json'

type SchemaBlockEditorProps = {
  value: string | undefined
  onChange: (value: string | undefined) => void
  contextWidgets?: SchemaWidget[]
}

// Only remove direct fields that mirror a conditional follower of the replaced
// production-type variant. Other direct fields (including custom ones) survive.
const removeReplacedProductionFollower = (
  previousRefs: string[],
  properties: Record<string, RJSFSchema>,
  required: string[]
) => {
  for (const ref of previousRefs) {
    const key = ref.replace('#/conditionals/', '')
    const block = conditionals[key as keyof typeof conditionals] as {
      then?: { properties?: Record<string, RJSFSchema> }
      else?: { properties?: Record<string, RJSFSchema> }
    }
    for (const [followerKey, follower] of Object.entries({
      ...block?.then?.properties,
      ...block?.else?.properties
    })) {
      if (properties[followerKey]?.$ref !== follower.$ref) continue
      delete properties[followerKey]
      const index = required.indexOf(followerKey)
      if (index !== -1) required.splice(index, 1)
    }
  }
}

export const SchemaBlockEditor = ({
  value,
  onChange,
  contextWidgets = []
}: SchemaBlockEditorProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  // 1. Get props from schema
  const properties = getPropertiesFromSchema(value)

  // 2. Get required properties from schema
  let requiredProperties: string[] = []
  try {
    requiredProperties = JSON.parse(value ?? '{}').required ?? []
  } catch (e) {
    console.error('Failed to parse schema for required properties', e)
  }

  // 3. Turn schema props to widgets
  const selectedWidgets = properties
    ? turnPropertiesToWidgets(properties, requiredProperties)
    : []

  // 4. Get rest of the selectable widgets
  const selectablePayloadWidgets = getSelectableWidgets(selectedWidgets)

  // 5. Sort widgets
  const sortedWidgets = sortWidgets(selectablePayloadWidgets)

  // 6. Group widgets based on propKey
  const groupedWidgets = groupWidgets(sortedWidgets)

  // 7. Split into added (selected/locked) and available (unselected)
  const addedGroups = groupedWidgets.filter((group) => {
    const active =
      group.widgets.find((w) => w.state === 'selected') ?? group.widgets[0]
    return active.state === 'selected' || active.state === 'locked'
  })

  const availableGroups = groupedWidgets.filter((group) => {
    const active =
      group.widgets.find((w) => w.state === 'selected') ?? group.widgets[0]
    return active.state === 'unselected'
  })

  // 8. Separate subSamples from payload groups
  const payloadGroups = addedGroups.filter((g) => g.id !== 'subSamples')
  const subSamplesGroup = addedGroups.find((g) => g.id === 'subSamples')
  const pickerGroups = availableGroups.filter((g) => g.id !== 'subSamples')
  const availableSubSampleGroup = availableGroups.find(
    (g) => g.id === 'subSamples'
  )

  // 9. Subsample field list (for DelprøverSection)
  const subSamplePropertyList = buildSubSamplePropertyList(value)

  // 8. Split contextWidgets around the 'payload' insertion point
  const payloadIndex = contextWidgets.findIndex((w) => w.propKey === 'payload')
  const splitAt = payloadIndex >= 0 ? payloadIndex : contextWidgets.length
  const topFixed = contextWidgets
    .slice(0, splitAt)
    .filter((w) => w.state !== 'hidden')
  const bottomFixed = contextWidgets
    .slice(splitAt + 1)
    .filter((w) => w.state !== 'hidden')

  const onAdd = (widgetId: string, isRequired: boolean) => {
    const widget = selectablePayloadWidgets.find((w) => w.id === widgetId)
    if (!widget) {
      console.error(`Widget with id ${widgetId} not found`)
      return
    }
    const currentFormData: RJSFSchema = JSON.parse(value ?? '{}')
    const existingProperties = { ...currentFormData.properties }
    const required = Array.isArray(currentFormData.required)
      ? [...currentFormData.required]
      : []
    let allOf = Array.isArray(currentFormData.allOf)
      ? [...currentFormData.allOf]
      : []

    // Build new properties ensuring subSamples is always last
    const isSubSamplesWidget = widget.propKey === 'subSamples'
    const newProperties: Record<string, RJSFSchema> = {}

    // First: all existing non-subSamples properties
    Object.entries(existingProperties).forEach(([key, val]) => {
      if (key !== 'subSamples') newProperties[key] = val as RJSFSchema
    })

    // Next: the new widget (if not subSamples).
    if (!isSubSamplesWidget && widget.schema) {
      newProperties[widget.propKey] = widget.schema
    }

    // Finally: subSamples always last (new or carried over)
    if (isSubSamplesWidget) {
      if (widget.schema) newProperties[widget.propKey] = widget.schema
    } else if (existingProperties['subSamples']) {
      newProperties['subSamples'] = existingProperties[
        'subSamples'
      ] as RJSFSchema
    }

    if (isRequired && !required.includes(widget.propKey)) {
      required.push(widget.propKey)
    } else if (!isRequired && required.includes(widget.propKey)) {
      const index = required.indexOf(widget.propKey)
      if (index > -1) {
        required.splice(index, 1)
      }
    }
    // When switching variants within a propKey group, remove any allOf refs
    // that belonged to the previously selected widget for this propKey so they
    // don't accumulate alongside the new widget's conditionals.
    // Use the full payloadWidgets list (incl. hidden) so that cross-variant
    // refs (e.g. produksjonstype ↔ produksjonstypeStorfe) are always cleaned up.
    const replacedWidgetIds = new Set(
      payloadWidgets
        .filter((w) => w.propKey === widget.propKey && w.id !== widget.id)
        .map((w) => w.id)
    )
    const replacedRefs = [...replacedWidgetIds].map(
      (id) => `#/conditionals/${id}`
    )
    const previousRefs = allOf
      .filter(
        (conditional) =>
          typeof conditional === 'object' &&
          conditional !== null &&
          '$ref' in conditional &&
          replacedRefs.includes(String(conditional.$ref))
      )
      .map((conditional) => String((conditional as { $ref: string }).$ref))
    allOf = allOf.filter(
      (conditional) =>
        !(
          typeof conditional === 'object' &&
          '$ref' in conditional &&
          replacedRefs.includes((conditional as { $ref: string }).$ref)
        )
    )
    if (widget.id === 'produksjonstypeAkva') {
      removeReplacedProductionFollower(previousRefs, newProperties, required)
    }

    if (widget.conditionals) {
      const existingRefs = new Set(
        allOf
          .filter(
            (conditional) =>
              typeof conditional === 'object' && '$ref' in conditional
          )
          .map((conditional) => (conditional as { $ref: string }).$ref)
      )
      const newConditionals = widget.conditionals.filter(
        (conditional) =>
          !(
            typeof conditional === 'object' &&
            '$ref' in conditional &&
            existingRefs.has((conditional as { $ref: string }).$ref)
          )
      )
      allOf = allOf.concat(newConditionals)

      // Remove any properties that are exposed via the widget's conditionals
      // (follower fields). They must not also exist as direct properties, or
      // RJSF will render them twice — once from the conditional branch and
      // once from the explicit property.
      for (const conditional of widget.conditionals) {
        if (typeof conditional !== 'object' || !('$ref' in conditional))
          continue
        const condRef = (conditional as { $ref: string }).$ref
        const condKey = condRef.replace('#/conditionals/', '')
        const block = conditionals[condKey as keyof typeof conditionals] as {
          then?: { properties?: Record<string, unknown> }
          else?: { properties?: Record<string, unknown> }
        }
        const followerKeys = new Set([
          ...Object.keys(block?.then?.properties ?? {}),
          ...Object.keys(block?.else?.properties ?? {})
        ])
        for (const followerKey of followerKeys) {
          delete newProperties[followerKey]
          const reqIdx = required.indexOf(followerKey)
          if (reqIdx > -1) required.splice(reqIdx, 1)
        }
      }
    }
    const newSchema: RJSFSchema = {
      ...currentFormData,
      properties: newProperties,
      required
    }
    if (allOf.length > 0) {
      newSchema.allOf = allOf
    } else {
      delete newSchema.allOf
    }
    onChange(JSON.stringify(newSchema, null, 2))
  }

  const onDelete = (widgetId: string) => {
    const widget = selectablePayloadWidgets.find((w) => w.id === widgetId)
    if (!widget) {
      console.error(`Widget with id ${widgetId} not found`)
      return
    }
    const currentFormData: RJSFSchema = JSON.parse(value ?? '{}')
    const properties = { ...currentFormData.properties }
    const ownedKeys = new Set([widget.propKey])

    const required = Array.isArray(currentFormData.required)
      ? currentFormData.required.filter((key) => !ownedKeys.has(key))
      : []
    const allOf = Array.isArray(currentFormData.allOf)
      ? currentFormData.allOf.filter(
          (conditional) =>
            !(
              typeof conditional === 'object' &&
              '$ref' in conditional &&
              (conditional as { $ref: string }).$ref ===
                `#/conditionals/${widget.id}`
            )
        )
      : []

    for (const key of ownedKeys) delete properties[key]

    const newSchema: RJSFSchema = {
      ...currentFormData,
      properties,
      required
    }
    if (allOf.length > 0) {
      newSchema.allOf = allOf
    } else {
      delete newSchema.allOf
    }
    onChange(JSON.stringify(newSchema, null, 2))
  }

  const onMoveUp = (propKey: string) => {
    const currentFormData: RJSFSchema = JSON.parse(value ?? '{}')
    const allEntries = Object.entries(currentFormData.properties ?? {})
    const subSamplesEntries = allEntries.filter(([key]) => key === 'subSamples')
    const payloadEntries = allEntries.filter(([key]) => key !== 'subSamples')
    const index = payloadEntries.findIndex(([key]) => key === propKey)
    if (index <= 0) return
    ;[payloadEntries[index - 1], payloadEntries[index]] = [
      payloadEntries[index],
      payloadEntries[index - 1]
    ]
    onChange(
      JSON.stringify(
        {
          ...currentFormData,
          properties: Object.fromEntries([
            ...payloadEntries,
            ...subSamplesEntries
          ])
        },
        null,
        2
      )
    )
  }

  const onMoveDown = (propKey: string) => {
    const currentFormData: RJSFSchema = JSON.parse(value ?? '{}')
    const allEntries = Object.entries(currentFormData.properties ?? {})
    const subSamplesEntries = allEntries.filter(([key]) => key === 'subSamples')
    const payloadEntries = allEntries.filter(([key]) => key !== 'subSamples')
    const index = payloadEntries.findIndex(([key]) => key === propKey)
    if (index < 0 || index >= payloadEntries.length - 1) return
    ;[payloadEntries[index], payloadEntries[index + 1]] = [
      payloadEntries[index + 1],
      payloadEntries[index]
    ]
    onChange(
      JSON.stringify(
        {
          ...currentFormData,
          properties: Object.fromEntries([
            ...payloadEntries,
            ...subSamplesEntries
          ])
        },
        null,
        2
      )
    )
  }

  return (
    <>
      {topFixed.map((widget) => (
        <FixedWidgetRow key={widget.id} widget={widget} />
      ))}
      {payloadGroups.length === 0 && topFixed.length === 0 && (
        <Mtds.Muted>
          Ingen elementer lagt til. Klikk «Legg til element» for å starte.
        </Mtds.Muted>
      )}
      {payloadGroups.map((group, index) => (
        <WidgetGroup
          key={group.id}
          group={group}
          isFirst={index === 0}
          isLast={index === payloadGroups.length - 1}
          onAdd={onAdd}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
        />
      ))}
      <Mtds.Button
        onClick={() => setIsPickerOpen(true)}
        data-variant={'secondary'}
      >
        <PlusIcon />
        Legg til element
      </Mtds.Button>
      <Mtds.Divider />
      <DelprøverSection
        subSamplesGroup={subSamplesGroup}
        availableSubSampleGroup={availableSubSampleGroup}
        subSamplePropertyList={subSamplePropertyList}
        onAdd={onAdd}
        onDelete={onDelete}
      />
      {bottomFixed.map((widget) => (
        <FixedWidgetRow key={widget.id} widget={widget} />
      ))}
      <SchemaElementPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        availableGroups={pickerGroups}
        onAdd={onAdd}
      />
    </>
  )
}

type FixedWidgetRowProps = { widget: SchemaWidget }

const FixedWidgetRow = ({ widget }: FixedWidgetRowProps) => (
  <ElementCard
    icon={getWidgetTypeIcon(widget.schema)}
    title={getWidgetTitle(widget)}
    description={getWidgetDescription(widget.schema)}
    includeInRequisition={widget.schema?.['x-include-in-requisition'] ?? false}
    isFallback={widget.isFallback}
    actions={
      <Mtds.Button data-color='neutral' data-tooltip='Låst felt' disabled>
        <LockIcon />
      </Mtds.Button>
    }
    endActions={
      <Mtds.Field
        as='input'
        label='Må fylles ut'
        type='checkbox'
        role='switch'
        checked={widget.isRequired}
        disabled
        onChange={() => undefined}
      />
    }
  />
)

type ElementCardProps = {
  icon?: ReactNode
  title: string
  description?: string
  subProperties?: Array<{ key: string; title: string }> | null
  includeInRequisition?: boolean
  isFallback?: boolean
  actions?: ReactNode
  endActions?: ReactNode
  children?: ReactNode
}

const ElementCard = ({
  icon,
  title,
  description,
  subProperties,
  includeInRequisition,
  isFallback,
  actions,
  endActions,
  children
}: ElementCardProps) => (
  <Mtds.Card className='flex flex-col gap-2'>
    <Mtds.Grid>
      <Mtds.Flex
        data-align={'center'}
        data-justify={'space-between'}
        data-nowrap
      >
        <Mtds.Flex data-align={'center'} data-gap={'2'} data-nowrap>
          {icon}
          <strong>{title}</strong>
          <Mtds.Tag
            data-color={includeInRequisition ? 'success' : 'neutral'}
            data-size='sm'
            data-icon='none'
          >
            {includeInRequisition ? 'Til lab' : 'Ikke til lab'}
          </Mtds.Tag>
          {isFallback && (
            <Mtds.Tag data-color='neutral' data-size='sm' data-icon='none'>
              Egendefinert felt
            </Mtds.Tag>
          )}
        </Mtds.Flex>
        {actions && (
          <Mtds.Flex data-align={'center'} data-gap={'1'} data-nowrap>
            {actions}
          </Mtds.Flex>
        )}
      </Mtds.Flex>
      {description && <Mtds.Muted>{description}</Mtds.Muted>}
      {subProperties && subProperties.length > 0 && (
        <Mtds.Grid data-gap='1'>
          <Mtds.Muted>
            <strong>Felter</strong>
          </Mtds.Muted>
          <Mtds.Flex data-gap='1' className='flex-wrap'>
            {subProperties.map((p) => (
              <Mtds.Tag
                key={p.key}
                data-size='sm'
                data-icon='none'
                data-color='neutral'
              >
                {p.title}
              </Mtds.Tag>
            ))}
          </Mtds.Flex>
        </Mtds.Grid>
      )}
      {(children || endActions) && (
        <Mtds.Flex data-align={'center'}>
          {children}
          {endActions && (
            <Mtds.Flex data-align={'center'} className='ml-auto'>
              {endActions}
            </Mtds.Flex>
          )}
        </Mtds.Flex>
      )}
    </Mtds.Grid>
  </Mtds.Card>
)

type WidgetIconButtonProps = {
  widgetId: string
  state: WidgetState
  onDelete: (widgetId: string) => void
}

const WidgetIconButton = ({
  widgetId,
  state,
  onDelete
}: WidgetIconButtonProps) => {
  if (state === 'locked') {
    return (
      <Mtds.Button data-color='neutral' data-tooltip='Låst felt' disabled>
        <LockIcon />
      </Mtds.Button>
    )
  }

  return (
    <Mtds.Button
      data-color='danger'
      data-tooltip='Fjern felt'
      onClick={() => onDelete(widgetId)}
    >
      <TrashIcon />
    </Mtds.Button>
  )
}

type WidgetGroupProps = {
  group: SchemaWidgetGroup
  isFirst: boolean
  isLast: boolean
  onDelete: (widgetId: string) => void
  onAdd: (widgetId: string, isRequired: boolean) => void
  onMoveUp: (propKey: string) => void
  onMoveDown: (propKey: string) => void
}

const WidgetGroup = ({
  group,
  isFirst,
  isLast,
  onDelete,
  onAdd,
  onMoveUp,
  onMoveDown
}: WidgetGroupProps) => {
  const firstWidget = group.widgets[0]
  const selectedWidget = group.widgets.find((w) => w.state === 'selected')
  const widget = selectedWidget || firstWidget
  const includeInRequisition =
    widget.schema?.['x-include-in-requisition'] ?? false
  const subProperties = getWidgetSubProperties(widget.schema)
  return (
    <WidgetRow
      widgetId={widget.id}
      state={widget.state}
      isRequired={widget.isRequired}
      isFirst={isFirst}
      isLast={isLast}
      title={group.title ?? group.id}
      icon={getWidgetTypeIcon(widget.schema)}
      description={getWidgetDescription(widget.schema)}
      subProperties={subProperties}
      includeInRequisition={includeInRequisition}
      isFallback={widget.isFallback}
      onDelete={onDelete}
      onAdd={onAdd}
      onMoveUp={() => onMoveUp(group.id)}
      onMoveDown={() => onMoveDown(group.id)}
    >
      {group.widgets.length > 1 && widget.state === 'selected' && (
        <Mtds.Field
          as='select'
          value={widget.id}
          options={group.widgets.map((w) => ({
            value: w.id,
            label: getWidgetTitle(w)
          }))}
          onChange={({ target }) => {
            onAdd(target.value, widget.isRequired)
          }}
        />
      )}
    </WidgetRow>
  )
}

type WidgetRowProps = {
  widgetId: string
  title: string
  icon?: ReactNode
  description?: string
  subProperties?: Array<{ key: string; title: string }> | null
  includeInRequisition?: boolean
  isFallback?: boolean
  state: WidgetState
  isRequired: boolean
  isFirst: boolean
  isLast: boolean
  onDelete: (widgetId: string) => void
  onAdd: (widgetId: string, isRequired: boolean) => void
  onMoveUp: () => void
  onMoveDown: () => void
  children?: ReactNode
}

const WidgetRow = ({
  widgetId,
  title,
  icon,
  description,
  subProperties,
  includeInRequisition,
  isFallback,
  state,
  isRequired,
  isFirst,
  isLast,
  onDelete,
  onAdd,
  onMoveUp,
  onMoveDown,
  children
}: WidgetRowProps) => (
  <ElementCard
    icon={icon}
    title={title}
    description={description}
    subProperties={subProperties}
    includeInRequisition={includeInRequisition}
    isFallback={isFallback}
    actions={
      <>
        <Mtds.Button
          data-color='neutral'
          data-tooltip='Flytt opp'
          disabled={isFirst}
          onClick={onMoveUp}
        >
          <ArrowUpIcon />
        </Mtds.Button>
        <Mtds.Button
          data-color='neutral'
          data-tooltip='Flytt ned'
          disabled={isLast}
          onClick={onMoveDown}
        >
          <ArrowDownIcon />
        </Mtds.Button>
        <WidgetIconButton
          widgetId={widgetId}
          state={state}
          onDelete={onDelete}
        />
      </>
    }
    endActions={
      state === 'selected' || isFallback ? (
        <Mtds.Field
          as='input'
          label='Må fylles ut'
          type='checkbox'
          role='switch'
          checked={isRequired}
          onChange={(e) => onAdd(widgetId, e.target.checked)}
        />
      ) : undefined
    }
  >
    {state === 'selected' && children}
  </ElementCard>
)

type DelprøverSectionProps = {
  subSamplesGroup: SchemaWidgetGroup | undefined
  availableSubSampleGroup: SchemaWidgetGroup | undefined
  subSamplePropertyList: SchemaProperty[] | undefined
  onAdd: (widgetId: string, isRequired: boolean) => void
  onDelete: (widgetId: string) => void
}

const DelprøverSection = ({
  subSamplesGroup,
  availableSubSampleGroup,
  subSamplePropertyList,
  onAdd,
  onDelete
}: DelprøverSectionProps) => {
  const selectedWidget = subSamplesGroup?.widgets.find(
    (w) => w.state === 'selected'
  )
  const widget = selectedWidget ?? subSamplesGroup?.widgets[0]
  const includeInRequisition =
    widget?.schema?.['x-include-in-requisition'] ?? false

  return (
    <Mtds.Card as={Mtds.Grid} data-gap='4'>
      <Mtds.Grid data-gap='0'>
        <Mtds.Heading data-size='xs'>Delprøver</Mtds.Heading>
        <Mtds.Muted>
          Noen prøver krever innsamling av delprøver. Her kan du velge skjemaet
          som brukes til å registrere disse.
        </Mtds.Muted>
      </Mtds.Grid>
      <Mtds.Divider data-gap='0' />
      {subSamplesGroup == null || widget == null ? (
        <Mtds.Flex data-align='center' data-justify='space-between'>
          <Mtds.Muted>Ingen delprøveskjema er lagt til.</Mtds.Muted>
          <Mtds.Button
            data-variant='secondary'
            disabled={availableSubSampleGroup == null}
            onClick={() => {
              if (availableSubSampleGroup) {
                onAdd(availableSubSampleGroup.widgets[0].id, true)
              }
            }}
          >
            <PlusIcon />
            Legg til delprøveskjema
          </Mtds.Button>
        </Mtds.Flex>
      ) : (
        <Mtds.Grid data-gap='3'>
          <Mtds.Flex
            data-align='center'
            data-justify='space-between'
            data-nowrap
          >
            <Mtds.Flex data-align='center' data-gap='2' data-nowrap>
              {getWidgetTypeIcon(widget.schema)}
              <strong>{subSamplesGroup.title ?? subSamplesGroup.id}</strong>
              <Mtds.Tag
                data-color={includeInRequisition ? 'success' : 'neutral'}
                data-size='sm'
                data-icon='none'
              >
                {includeInRequisition ? 'Til lab' : 'Ikke til lab'}
              </Mtds.Tag>
            </Mtds.Flex>
            <Mtds.Button
              data-color='danger'
              data-tooltip='Fjern delprøver'
              onClick={() => onDelete(widget.id)}
            >
              <TrashIcon />
            </Mtds.Button>
          </Mtds.Flex>
          {getWidgetDescription(widget.schema) && (
            <Mtds.Muted>{getWidgetDescription(widget.schema)}</Mtds.Muted>
          )}
          {subSamplePropertyList && subSamplePropertyList.length > 0 && (
            <Mtds.Grid data-gap='1'>
              <Mtds.Muted>
                <strong>Felter</strong>
              </Mtds.Muted>
              <Mtds.Flex data-gap='1' className='flex-wrap'>
                {subSamplePropertyList.map((prop) => (
                  <Mtds.Tag
                    key={prop.propertyKey}
                    data-size='sm'
                    data-icon='none'
                    data-color='neutral'
                  >
                    {prop.title ?? prop.propertyKey}
                  </Mtds.Tag>
                ))}
              </Mtds.Flex>
            </Mtds.Grid>
          )}
          <Mtds.Flex data-align='center' data-justify='space-between'>
            {subSamplesGroup.widgets.length > 1 && (
              <Mtds.Field
                as='select'
                value={widget.id}
                options={subSamplesGroup.widgets.map((w) => ({
                  value: w.id,
                  label: getWidgetTitle(w)
                }))}
                onChange={({ target }) => {
                  onAdd(target.value, widget.isRequired)
                }}
              />
            )}
            <Mtds.Flex data-align='center' className='ml-auto'>
              <Mtds.Field
                as='input'
                label='Må fylles ut'
                type='checkbox'
                role='switch'
                checked={widget.isRequired}
                onChange={(e) => onAdd(widget.id, e.target.checked)}
              />
            </Mtds.Flex>
          </Mtds.Flex>
        </Mtds.Grid>
      )}
    </Mtds.Card>
  )
}
